import { useEffect, useState, RefObject } from 'react';

/**
 * Hook do obliczania liczby stron A4 na podstawie wysokości zawartości
 *
 * @param containerRef - Ref do elementu kontenera, którego wysokość ma być zmierzona
 * @param dependencies - Opcjonalne zależności, które wywołają ponowne przeliczenie
 * @param excludeSelectors - Selektory elementów ukrytych w druku — ich wysokość zostanie odjęta
 * @returns Liczba stron A4 potrzebnych do wyświetlenia zawartości
 */
export const usePagesCount = (
  containerRef: RefObject<HTMLElement>,
  dependencies: unknown[] = [],
  excludeSelectors: string[] = []
): number => {
  const [pagesCount, setPagesCount] = useState<number>(0);

  useEffect(() => {
    const calculatePages = () => {
      if (!containerRef.current) {
        return;
      }

      // Pobierz wysokość zawartości i odejmij wysokość elementów ukrywanych w druku
      const excludedHeight = excludeSelectors.reduce((sum, selector) => {
        const el = containerRef.current?.querySelector<HTMLElement>(selector);
        return sum + (el?.offsetHeight ?? 0);
      }, 0);

      const contentHeight = containerRef.current.scrollHeight - excludedHeight;

      // Wymiary A4 w pikselach przy 96 DPI (1mm ≈ 3.7795px)
      const MM_TO_PX = 96 / 25.4;
      const A4_WIDTH_PX = 210 * MM_TO_PX;          // ~794px
      const A4_HEIGHT_PX = 297 * MM_TO_PX;         // ~1123px

      // Marginesy strony z @media print (@page margin) — patrz src/styles/raport.css
      const PRINT_MARGIN_MM = 10;
      const PRINT_MARGINS_PX = 2 * PRINT_MARGIN_MM * MM_TO_PX; // ~75px

      // Drukowalny obszar A4 po odjęciu marginesów
      const printableWidthPx = A4_WIDTH_PX - PRINT_MARGINS_PX;   // ~718px
      const printableHeightPx = A4_HEIGHT_PX - PRINT_MARGINS_PX; // ~1047px

      // W druku layout renderowany jest ze sztywną szerokością `PRINT_HTML_WIDTH_PX`
      // (patrz src/styles/raport.css → html/body `width`). Przeglądarka dopasowuje tę
      // szerokość do drukowalnej szerokości A4, więc realna skala druku zależy WYŁĄCZNIE
      // od geometrii (printableWidth / szerokość layoutu), a NIE od `zoom` z CSS — przy
      // dopasowaniu do szerokości strony `zoom` się skraca. Dzięki temu zmiana `zoom`
      // w raport.css (np. pod czytelność) nie wymaga żadnych zmian w tym przeliczniku.
      const PRINT_HTML_WIDTH_PX = 1550;
      const printScale = printableWidthPx / PRINT_HTML_WIDTH_PX; // ~0.463

      const scaledContentHeight = contentHeight * printScale;

      // Oblicz liczbę stron (zaokrąglij w górę)
      const calculatedPages = Math.ceil(scaledContentHeight / printableHeightPx);

      // Ustaw minimalnie 1 stronę
      setPagesCount(Math.max(1, calculatedPages));
    };

    // Oblicz od razu
    calculatePages();

    // Dodaj listener na resize okna dla responsywności
    const handleResize = () => {
      calculatePages();
    };

    window.addEventListener('resize', handleResize);

    // Użyj ResizeObserver dla bardziej precyzyjnego śledzenia zmian rozmiaru zawartości
    let resizeObserver: ResizeObserver | null = null;

    if (containerRef.current && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        calculatePages();
      });

      resizeObserver.observe(containerRef.current);
    }

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [containerRef, ...dependencies]);

  return pagesCount;
};
